import { queryOptions, skipToken } from '@tanstack/react-query';
import orderBy from 'lodash/orderBy';

import { LegalEntityBranchData } from 'chaika-frontend-types/frontend-cms';

import { LegalEntityBranchApi } from '@organization/api/LegalEntityBranchApi';
import { queryClient } from '@store/queryClient';
import { LegalEntityBranchDataWithEndData, LegalEntityBranchStatus } from '@organization/store/legalEntityBranch/types';
import { DateHelper, DateType } from '@utils/DateHelper';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';

const STALE_TIME_MS = Infinity;

export const legalEntityBranchKeys = {
    all: ['legalEntityBranch'] as const,
    lists: () => [...legalEntityBranchKeys.all, 'list'] as const,
    byOrganization: (organizationId: string) => [...legalEntityBranchKeys.lists(), { organizationId }] as const,
    byOrganizations: (organizationIds: string[]) =>
        [...legalEntityBranchKeys.lists(), { organizationIds: getUniqSortedIds(organizationIds) }] as const,
    byLegalEntity: (legalEntityId: string) => [...legalEntityBranchKeys.lists(), { legalEntityId }] as const,
};

export const legalEntityBranchQueryOptions = {
    byOrganizationId: (organizationId: string | undefined) =>
        queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: legalEntityBranchKeys.byOrganization(organizationId ?? ''),
            queryFn: organizationId ? () => LegalEntityBranchApi.getList({ organizationId }) : skipToken,
        }),
    // end/status need the org's next attachment (possibly another LE), so fetch by the LE's orgs, enrich, then filter back.
    byLegalEntityId: (legalEntityId: string | undefined) =>
        queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: legalEntityBranchKeys.byLegalEntity(legalEntityId ?? ''),
            queryFn: legalEntityId
                ? async (): Promise<LegalEntityBranchDataWithEndData[]> => {
                      const ownBranches = await LegalEntityBranchApi.getList({ legalEntityId });
                      const organizationIds = ownBranches.map(branch => branch.organizationId);

                      if (organizationIds.length === 0) return [];

                      const organizationBranches = await queryClient.fetchQuery(
                          legalEntityBranchQueryOptions.byOrganizationIds(organizationIds),
                      );

                      return makeBranchesWithEndData(organizationBranches).filter(
                          branch => branch.legalEntityId === legalEntityId,
                      );
                  }
                : skipToken,
        }),
    // Raw branches for the given organizations, used to resolve each organization's current owner legal entity.
    byOrganizationIds: (organizationIds: string[]) => {
        const normalizedIds = getUniqSortedIds(organizationIds);

        return queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: legalEntityBranchKeys.byOrganizations(normalizedIds),
            queryFn:
                normalizedIds.length > 0
                    ? () => LegalEntityBranchApi.getList({ organizationId: normalizedIds.join(',') })
                    : skipToken,
        });
    },
};

// Derives each branch's `end` (start of the next attachment for the same organization) and `status`.
// Assumes the input contains every branch for the organizations involved.
export function makeBranchesWithEndData(
    branches: (LegalEntityBranchData | LegalEntityBranchDataWithEndData)[],
): LegalEntityBranchDataWithEndData[] {
    const currentEndDate = new Map<string, string>();
    const orderedBranches = orderBy(branches, ['start', 'createdAt'], ['desc', 'desc']);

    return orderedBranches.map(branch => {
        const end = currentEndDate.get(branch.organizationId);
        currentEndDate.set(branch.organizationId, branch.start);

        return { ...branch, end, status: calculateBranchStatus(branch.start, end) };
    });
}

function calculateBranchStatus(start: string, end?: string): LegalEntityBranchStatus {
    const now = DateHelper.now();

    return !!end && DateHelper.isBefore(end, now)
        ? LegalEntityBranchStatus.Archive
        : DateHelper.isAfter(start, now)
          ? LegalEntityBranchStatus.Planned
          : LegalEntityBranchStatus.Active;
}

// The legal entity active for an organization on a given day. Assumes the branches are already scoped to a single organization.
export function getActualOnDayLegalEntityId(branches: LegalEntityBranchData[], actualOn: DateType): string | undefined {
    return orderBy(branches, 'start', 'desc').find(({ start }) => DateHelper.isSameOrAfter(actualOn, start))
        ?.legalEntityId;
}
